<template>
  <Modal
    ref="modal"
    form
    :modal-style="{ width: '40vw', 'max-height': '100%' }"
    title="Manually Send Scheduled Report"
    @submit="confirmSend"
  >
    <div class="column q-gutter-lg q-mb-lg">
      <InputWrapper v-model="selectedSchedule">
        <template #label>
          <div class="field-heading text-primary">Select Schedule</div>
          <span class="field-subtext">
            Affects the selectable delivery times and content range for the
            report
          </span>
        </template>

        <template #control>
          <InputSelect
            v-model="selectedSchedule"
            emit-value
            new-design
            option-label="label"
            option-prop="value"
            :options="scheduleOptions"
          />
        </template>
      </InputWrapper>

      <div class="row items-end justify-between">
        <InputWrapper
          v-model="selectedTimestamp"
          class="q-pr-md col-auto"
        >
          <template #label>
            <div class="field-heading text-primary">
              Select Report to Resend:
            </div>
            <span class="field-subtext">
              Affects the time range for the content contained in the report.
            </span>
          </template>

          <template #control>
            <InputSelect
              v-model="selectedTimestamp"
              :disabled="sendNow || !selectedSchedule"
              emit-value
              new-design
              option-label="label"
              option-prop="value"
              :options="timestampOptions"
              :placeholder="timestampPlaceholder"
            />
          </template>
        </InputWrapper>
        <InputWrapper
          v-model="sendNow"
          class="row col-auto items-center"
        >
          <template #label>
            <div class="field-heading text-primary">Send Now</div>
          </template>

          <template #control>
            <QToggle
              v-model="sendNow"
              :disable="!selectedSchedule"
              emit-value
              new-design
              option-label="label"
              option-prop="value"
              :options="timestampOptions"
              :placeholder="timestampPlaceholder"
            />
          </template>
        </InputWrapper>
      </div>

      <div
        v-if="!sendDisabled"
        class="q-pa-smd bg-ds-denim-2 q-mt-smd col-auto flex-1"
      >
        Scheduled report will send with content for the
        <strong>{{ selectedSchedule.mentionsPeriod() }}</strong>
        leading up to <strong>{{ formatTimestamp(sendingTimestamp) }}</strong>
      </div>
    </div>

    <template #buttons="{ close }">
      <BaseButton
        aria-label="Cancel"
        class="q-mr-smd"
        color="primary"
        flat
        label="Cancel"
        @click="close"
      />
      <BaseButton
        aria-label="Send"
        color="ds-red-4"
        :disabled="sendDisabled"
        label="Send"
        type="submit"
      />
    </template>
  </Modal>
</template>

<script>
import { storeToRefs } from "pinia";

import { BaseButton, InputSelect, InputWrapper } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import {
  formatDate,
  fromCurrentToGivenTimezone,
  parseDate,
  shortTimezone,
  startOfDateInTimezone,
  subtractTime,
  unixDateTime,
} from "shared/helpers/date";
import { pluralize } from "shared/helpers/string";

import { useUserStore } from "src/stores/user";

const PREVIOUS_DELIVERIES_LIMIT = 5;

export default {
  name: "ManuallySendScheduledReportModal",
  components: {
    BaseButton,
    InputSelect,
    InputWrapper,
    Modal,
  },
  props: {
    specification: {
      type: Object,
      required: true,
    },
    schedules: {
      type: Array,
      required: true,
    },
  },
  setup() {
    const userStore = useUserStore();
    const { organisation } = storeToRefs(userStore);

    return { organisation };
  },
  data() {
    return {
      selectedSchedule: null,
      selectedTimestamp: null,
      sendNow: false,
    };
  },
  computed: {
    specTimezone() {
      return this.specification.user?.timeZone || this.organisation.timeZone;
    },
    scheduleOptions() {
      return this.schedules.map((schedule) => ({
        value: schedule,
        label: this.scheduleLabel(schedule),
      }));
    },
    twelveMonthsAgo() {
      return unixDateTime(
        startOfDateInTimezone(subtractTime(new Date(), 12, "month"), "day")
      );
    },
    timestampOptions() {
      if (!this.selectedSchedule) return [];

      const timestampOptions = [];

      const yearAgo = new Date();
      yearAgo.setDate(yearAgo.getDate() - 365);

      this.selectedSchedule
        .previousDeliveryTimestamps(PREVIOUS_DELIVERIES_LIMIT, {
          timezone: this.specTimezone,
        })
        .forEach((timestamp) => {
          if (timestamp >= this.twelveMonthsAgo) {
            timestampOptions.push({
              value: timestamp,
              label: this.formatTimestamp(timestamp),
            });
          }
        });

      return timestampOptions;
    },
    sendingTimestamp() {
      return this.sendNow ? unixDateTime() : this.selectedTimestamp;
    },
    sendDisabled() {
      return (
        !this.selectedSchedule || (!this.sendNow && !this.selectedTimestamp)
      );
    },
    timestampPlaceholder() {
      if (!this.selectedSchedule) return "Please select a schedule first";

      return "Select a delivery time";
    },
  },
  watch: {
    selectedSchedule() {
      this.selectedTimestamp = null;
    },
    sendNow() {
      if (this.sendNow) this.selectedTimestamp = null;
    },
  },
  methods: {
    scheduleLabel(schedule) {
      return [
        schedule.deliveryDays({ timezone: this.specTimezone }),
        schedule.deliveryTime({
          timezone: this.specTimezone,
          displayTimezone: true,
        }),
        schedule.mentionsPeriod(),
      ].join(" - ");
    },
    formatTimestamp(timestamp) {
      const date = fromCurrentToGivenTimezone(
        parseDate(timestamp),
        this.specTimezone
      );

      const timezoneString = shortTimezone(date, this.specTimezone);

      return formatDate(date, `h:mmaaa '${timezoneString} on' EEEE dd/LL/yyyy`);
    },
    confirmSend() {
      this.$modals.open("ConfirmModal", {
        props: {
          modelValue: true,
          title: "Confirm sending report",
          titleClass: "font-weight-bold",
          cancelLabel: "Cancel",
          cancelButtonProps: {
            unelevated: true,
            color: "hover-background",
            textColor: "ds-denim-9",
            class: "q-mr-md font-weight-normal-important",
          },
          confirmButtonProps: {
            unelevated: true,
            color: "ds-red-5",
            class: "font-weight-normal-important",
          },
          confirmLabel: "Send",
          dialogStyle: { maxWidth: "350px" },
          newDesign: true,
          html: true,
          message: `
            <div class="text-center">
              Scheduled report will send to <strong>${
                this.specification.recipients.length
              }
              ${pluralize(
                this.specification.recipients.length,
                "recipient"
              )}</strong>
              with content for the <strong>${this.selectedSchedule.mentionsPeriod()}</strong>
              leading up to <strong>${this.formatTimestamp(
                this.sendingTimestamp
              )}</strong>
              <br>Are you sure?
            </div>
          `,
        },
        events: {
          confirm: () => {
            this.$modals.close("ConfirmDialog");
            this.send();
          },
          cancel: () => {
            this.$modals.close("ConfirmDialog");
          },
        },
      });
    },
    async send() {
      try {
        const params = {
          scheduled_report_specification_schedule_id: this.selectedSchedule.id,
          timestamp: this.sendingTimestamp,
        };

        await this.$streemApiAdmin.post(
          `scheduled_report_specifications/${this.specification.id}/manual_send`,
          { params }
        );

        this.$refs.modal.close();
        this.$alert("Scheduled report triggered successfully");
      } catch (error) {
        this.$error("An error occured when triggering the manual send", error);
      }
    },
  },
};
</script>
